import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ProgressPanel from './ProgressPanel';

const renderModernPoint = (props) => {
  const { cx, cy } = props;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={10} fill="rgba(122,214,232,0.1)" />
      <rect
        x={cx - 5}
        y={cy - 5}
        width={10}
        height={10}
        fill="#7ad6e8"
        stroke="#fff"
        strokeWidth={2}
        transform={`rotate(45 ${cx} ${cy})`}
      />
    </g>
  );
};

const CustomIntervalBar = (props) => {
  const { x, y, width, height, fill } = props;
  if (!Number.isFinite(x) || !Number.isFinite(width)) return null;

  const visualThickness = 2;
  const capHeight = 10;
  const centerY = y + height / 2;

  return (
    <g>
      {/* Hit area */}
      <rect x={x} y={y} width={width} height={height} fill="transparent" />
      {/* Main line */}
      <rect
        x={x}
        y={centerY - visualThickness / 2}
        width={width}
        height={visualThickness}
        fill={fill}
      />
      {/* Left Cap */}
      <rect
        x={x}
        y={centerY - capHeight / 2}
        width={2}
        height={capHeight}
        fill={fill}
      />
      {/* Right Cap */}
      <rect
        x={x + width - 2}
        y={centerY - capHeight / 2}
        width={2}
        height={capHeight}
        fill={fill}
      />
    </g>
  );
};

const buildForestRows = ({
  t,
  fitResults,
  highResResults,
  includeHighRes = true,
  solowIntervalMode = 'profile',
}) => {
  const rows = [];
  const highResSuffix = t('estimationPage.forestHighResSuffix');
  const solowProfileLabel = t('estimationPage.forestSolowProfile');
  const solowBootstrapLabel = t('estimationPage.forestSolowBootstrap');

  const pushRow = (label, estimate, ci, meta = {}) => {
    if (!Number.isFinite(estimate)) return;
    const hasCI = Number.isFinite(ci?.lower) && Number.isFinite(ci?.upper);
    rows.push({
      label,
      estimate,
      range: hasCI ? [ci.lower, ci.upper] : null,
      ciLower: ci?.lower ?? null,
      ciUpper: ci?.upper ?? null,
      ...meta,
    });
  };

  const solowTotal = fitResults?.solowSmith?.total;
  if (Number.isFinite(solowTotal)) {
    if (solowIntervalMode === 'bootstrap') {
      pushRow(
        `${t('estimationPage.modelSolowSmith')} (${solowBootstrapLabel})`,
        solowTotal,
        fitResults?.solowSmith?.bootstrapCI
      );
    } else if (solowIntervalMode === 'both') {
      pushRow(
        `${t('estimationPage.modelSolowSmith')} (${solowProfileLabel})`,
        solowTotal,
        fitResults?.solowSmith?.ci
      );
      pushRow(
        `${t('estimationPage.modelSolowSmith')} (${solowBootstrapLabel})`,
        solowTotal,
        fitResults?.solowSmith?.bootstrapCI
      );
    } else {
      pushRow(
        `${t('estimationPage.modelSolowSmith')} (${solowProfileLabel})`,
        solowTotal,
        fitResults?.solowSmith?.ci
      );
    }
  }

  const logisticTotal = fitResults?.logistic?.params?.L;
  if (Number.isFinite(logisticTotal)) {
    pushRow(t('estimationPage.modelLogistic'), logisticTotal, fitResults?.logistic?.ci);
  }

  const gompertzTotal = fitResults?.gompertz?.params?.a;
  if (Number.isFinite(gompertzTotal)) {
    pushRow(t('estimationPage.modelGompertz'), gompertzTotal, fitResults?.gompertz?.ci);
  }

  if (includeHighRes && highResResults) {
    const highResSolow = highResResults.solowSmith;
    if (Number.isFinite(highResSolow?.total)) {
      pushRow(
        `${t('estimationPage.modelSolowSmith')} (${highResSuffix})`,
        highResSolow.total,
        highResSolow.ci,
        {
          ciPartial: Boolean(highResSolow.ci && highResSolow.ciComplete === false),
        }
      );
    }

    const highResLogistic = highResResults.logistic;
    if (Number.isFinite(highResLogistic?.total)) {
      pushRow(
        `${t('estimationPage.modelLogistic')} (${highResSuffix})`,
        highResLogistic.total,
        highResLogistic.ci,
        {
          ciPartial: Boolean(highResLogistic.ci && highResLogistic.ciComplete === false),
        }
      );
    }

    const highResGompertz = highResResults.gompertz;
    if (Number.isFinite(highResGompertz?.total)) {
      pushRow(
        `${t('estimationPage.modelGompertz')} (${highResSuffix})`,
        highResGompertz.total,
        highResGompertz.ci,
        {
          ciPartial: Boolean(highResGompertz.ci && highResGompertz.ciComplete === false),
        }
      );
    }
  }

  return rows;
};

const ForestTooltip = ({ active, payload, t, formatNumber }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        backgroundColor: 'rgba(30, 30, 30, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {data.label}
      </Typography>
      <Typography variant="body2">
        {t('estimationPage.estimatedTotal')}: {formatNumber(data.estimate)}
      </Typography>
      {Number.isFinite(data.ciLower) && Number.isFinite(data.ciUpper) && (
        <Typography variant="body2">
          {t('estimationPage.confidenceInterval')}: {formatNumber(data.ciLower)} - {formatNumber(data.ciUpper)}
        </Typography>
      )}
      {data.ciPartial && (
        <Typography variant="caption" color="text.secondary">
          {t('estimationPage.highResCIPartialNote')}
        </Typography>
      )}
    </Box>
  );
};

const ForestPlot = ({
  t,
  fitResults,
  highResResults,
  currentCount,
  includeHighRes,
  intervalMode,
  sortOrder = 'name',
  onIncludeHighResChange,
  onIntervalModeChange,
  onSortOrderChange,
  highResAvailable,
  loading,
  error,
  formatNumber,
  fitStage,
  fitProgress,
  targetUnitLabel,
}) => {
  const rows = useMemo(() => {
    const rawRows = buildForestRows({
      t,
      fitResults,
      highResResults,
      includeHighRes,
      solowIntervalMode: intervalMode,
    });

    if (sortOrder === 'estimateDesc') {
      return rawRows.sort((a, b) => (b.estimate || 0) - (a.estimate || 0));
    }
    // Default to name sort
    return rawRows.sort((a, b) => a.label.localeCompare(b.label));
  }, [t, fitResults, highResResults, includeHighRes, intervalMode, sortOrder]);

  const height = Math.max(260, rows.length * 56);

  const xMax = useMemo(() => {
    const candidates = [currentCount];
    rows.forEach((row) => {
      if (Number.isFinite(row.ciUpper)) {
        candidates.push(row.ciUpper);
      } else if (Number.isFinite(row.estimate)) {
        candidates.push(row.estimate);
      }
    });
    const max = Math.max(...candidates.filter(Number.isFinite));
    return Number.isFinite(max) ? Math.ceil(max * 1.08) : 'auto';
  }, [rows, currentCount]);

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssessmentIcon sx={{ mr: 1 }} />
          <Typography variant="h6">{t('estimationPage.forestTitle')}</Typography>
        </Box>
        {targetUnitLabel && (
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
            — {targetUnitLabel}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('estimationPage.forestDescription')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={includeHighRes}
              onChange={(event) => onIncludeHighResChange(event.target.checked)}
              disabled={!highResAvailable}
            />
          )}
          label={t('estimationPage.forestIncludeHighRes')}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t('estimationPage.forestSolowIntervalLabel')}</InputLabel>
          <Select
            value={intervalMode}
            label={t('estimationPage.forestSolowIntervalLabel')}
            onChange={(event) => onIntervalModeChange(event.target.value)}
          >
            <MenuItem value="profile">{t('estimationPage.forestSolowProfile')}</MenuItem>
            <MenuItem value="bootstrap">{t('estimationPage.forestSolowBootstrap')}</MenuItem>
            <MenuItem value="both">{t('estimationPage.forestSolowBoth')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('estimationPage.forestSortLabel')}</InputLabel>
          <Select
            value={sortOrder}
            label={t('estimationPage.forestSortLabel')}
            onChange={(event) => onSortOrderChange && onSortOrderChange(event.target.value)}
          >
            <MenuItem value="name">{t('estimationPage.forestSortName')}</MenuItem>
            <MenuItem value="estimateDesc">{t('estimationPage.forestSortEstimate')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {rows.length > 0 ? (
        <Box sx={{ position: 'relative' }}>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart layout="vertical" data={rows} margin={{ top: 32, right: 24, left: 12, bottom: 16 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="estimate"
                domain={[0, xMax]}
                tickFormatter={formatNumber}
                tick={{ fill: 'rgba(255,255,255,0.82)', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.22)' }}
                tickLine={{ stroke: 'rgba(255,255,255,0.18)' }}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={220}
                tick={{ fill: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 500 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.22)' }}
                tickLine={{ stroke: 'rgba(255,255,255,0.18)' }}
              />
              <Tooltip content={<ForestTooltip t={t} formatNumber={formatNumber} />} />
              {Number.isFinite(currentCount) && currentCount > 0 && (
                <ReferenceLine
                  x={currentCount}
                  strokeDasharray="6 4"
                  stroke="#ffab91"
                  label={{
                    value: t('estimationPage.currentCount'),
                    position: 'insideTop',
                    fill: '#ffab91',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                />
              )}
              <Bar
                dataKey="range"
                barSize={20}
                fill="#7ad6e8"
                shape={<CustomIntervalBar />}
                isAnimationActive={false}
              />
              <Line
                dataKey="estimate"
                stroke="none"
                dot={renderModernPoint}
                activeDot={false}
                isAnimationActive={false}
                name={t('estimationPage.estimatedTotal')}
              />
            </ComposedChart>
          </ResponsiveContainer>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
                zIndex: 1,
              }}
            >
              <ProgressPanel
                size={24}
                title={t('estimationPage.analysisInProgress')}
                stage={fitStage}
                progress={fitProgress}
                containerSx={{ width: '100%', maxWidth: 360, px: 2 }}
              />
            </Box>
          )}
        </Box>
      ) : (
        <>
          {loading ? (
            <Box sx={{ width: '100%', py: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Skeleton variant="text" width={120} height={32} />
                <Skeleton variant="text" width={100} height={32} />
              </Box>
              <Skeleton variant="rectangular" height={240} sx={{ mb: 2, borderRadius: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                <Skeleton variant="text" width={60} />
                <Skeleton variant="text" width={60} />
                <Skeleton variant="text" width={60} />
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">
              {error || t('noData')}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default ForestPlot;
